// ResponsiveTopBar.js
import React, { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  Badge,
  Avatar,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Autocomplete from "@mui/material/Autocomplete";
import InputBase from "@mui/material/InputBase";
import axios from "axios";
import logo from "../content/images/StonksCentral_logo_no_background.png";
import { useTheme } from "./ThemeContext";
import { AuthContext } from "../context/AuthContext";
import { PageContext } from "../context/PageContext";
import Cookies from "js-cookie";
import CommunitySearch from "./CommunitySearch";
import Utils from "../utils/Utils";
import { getImageUrl } from "../utils/imageUrl";

const ResponsiveTopBar = ({ isMobile, onMobileMenuToggle }) => {
  const { theme } = useTheme();
  const { isLoggedIn, setIsLoggedIn, user, signOut } = useContext(AuthContext);
  const { viewMode, setViewMode } = useContext(PageContext);
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [anchorElCreate, setAnchorElCreate] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [mobileSearchResults, setMobileSearchResults] = useState([]);

  const handleSearchSelect = (name) => {
    navigate(`/community/${name}`);
  };

  const handleLogout = () => {
    Cookies.remove("accessToken");
    Cookies.remove("user");
    setIsLoggedIn(false);
    navigate("/signin");
  };

  const handleOpenCreateMenu = (event) => {
    setAnchorElCreate(event.currentTarget);
  };

  const handleCloseCreateMenu = () => {
    setAnchorElCreate(null);
  };

  const avatarUrl = user && user.avatarUrl ? getImageUrl(user.avatarUrl) : null;
  const token = user?.token;

  const fetchNotifications = useCallback(() => {
    if (!isLoggedIn || !token) return;

    fetch(`${process.env.REACT_APP_SC_API_BASE_URL}/api/notifications`, {
      method: "GET",
      headers: {
        accept: "*/*",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.status === 401) {
          signOut();
          throw new Error("Unauthorized");
        }
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => setNotifications(data))
      .catch((error) => console.error("Error fetching notifications:", error));
  }, [isLoggedIn, token, signOut]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setDrawerOpen(open);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).slice(0, text.lastIndexOf(" ")) + "...";
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/notifications/read`,
        {
          method: "PUT",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(unreadIds),
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (notification.postId && notification.communityName) {
      navigate(`/community/${notification.communityName}/${notification.postId}`);
    } else if (notification.postId) {
      navigate(`/post/${notification.postId}`);
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/notifications/read`,
        {
          method: "PUT",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify([notification.id]),
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const unreadNotifications = notifications.filter((n) => !n.read);

  // Mobile search fetch
  useEffect(() => {
    if (mobileSearchQuery.length < 1) { setMobileSearchResults([]); return; }
    axios.get(`${process.env.REACT_APP_SC_API_BASE_URL}/api/community/search?query=${mobileSearchQuery}`)
      .then((r) => setMobileSearchResults(r.data))
      .catch(() => {});
  }, [mobileSearchQuery]);

  // ─── Notification drawer content ────────────────────────────────────────────
  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "360px",
        backgroundColor: "#0f1419",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #1e2939",
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: "#0f1419",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            background: "linear-gradient(90deg, #05df72 0%, #00bc7d 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "-0.02em",
          }}
        >
          Notifications
          {unreadNotifications.length > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 1.5,
                minWidth: 20,
                height: 20,
                borderRadius: "10px",
                backgroundColor: "#05df72",
                color: "#0a0e14",
                fontSize: "0.7rem",
                fontWeight: 700,
                WebkitTextFillColor: "#0a0e14",
                px: "6px",
              }}
            >
              {unreadNotifications.length}
            </Box>
          )}
        </Typography>
        <IconButton
          onClick={toggleDrawer(false)}
          size="small"
          sx={{
            color: "#6a7282",
            borderRadius: "10px",
            p: 0.75,
            "&:hover": { backgroundColor: "#1a1f26", color: "#d1d5dc" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Mark all as read */}
      {unreadNotifications.length > 0 && (
        <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
          <Button
            fullWidth
            onClick={markAllAsRead}
            sx={{
              backgroundColor: "rgba(0,201,80,0.08)",
              border: "1px solid rgba(0,201,80,0.2)",
              color: "#05df72",
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 500,
              fontSize: "0.8rem",
              py: 0.75,
              "&:hover": { backgroundColor: "rgba(0,201,80,0.14)" },
            }}
          >
            Mark all as read
          </Button>
        </Box>
      )}

      {/* Notifications list */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2.5, py: 2, "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#1e2939", borderRadius: 2 } }}>
        {notifications.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ color: "#4a5565", fontSize: "0.875rem" }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
            {notifications.map((notification, index) => {
              const { postTitle, commentText, count, notificationType, read } = notification;
              const truncatedPostTitle = truncateText(postTitle, 35);
              const truncatedCommentText = truncateText(commentText, 35);
              const typeText =
                notificationType === "reply"
                  ? count > 1 ? "replies" : "reply"
                  : count > 1 ? "likes" : "like";
              const displayText = commentText
                ? `Your comment`
                : `Your post`;
              const contentTitle = truncatedCommentText || truncatedPostTitle;

              return (
                <ListItem
                  key={index}
                  onClick={() => handleNotificationClick(notification)}
                  disablePadding
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: "12px",
                    borderRadius: "12px",
                    border: read ? "1px solid #1e2939" : "1px solid rgba(0,201,80,0.2)",
                    backgroundColor: read ? "#1a1f26" : "rgba(0,201,80,0.06)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: read ? "#242930" : "rgba(0,201,80,0.1)", borderColor: read ? "#2a3040" : "rgba(0,201,80,0.3)" },
                  }}
                >
                  {/* Unread dot */}
                  <Box sx={{ pt: "5px", flexShrink: 0 }}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: read ? "#2a3040" : "#05df72",
                        boxShadow: read ? "none" : "0 0 6px rgba(5,223,114,0.5)",
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: read ? "#99a1af" : "#d1d5dc",
                        fontWeight: read ? 400 : 500,
                        lineHeight: 1.5,
                        mb: 0.25,
                      }}
                    >
                      {displayText}{" "}
                      <Box component="span" sx={{ color: "#05df72", fontWeight: 600 }}>
                        "{contentTitle}"
                      </Box>{" "}
                      got{" "}
                      <Box component="span" sx={{ color: "#05df72", fontWeight: 600 }}>
                        {count} new {typeText}
                      </Box>
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid #1e2939",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          backgroundColor: "#0f1419",
        }}
      >
        <Button
          fullWidth
          onClick={() => { navigate("/profile/" + user?.username); setDrawerOpen(false); }}
          sx={{
            backgroundColor: "#1a1f26",
            border: "1px solid #1e2939",
            color: "#d1d5dc",
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 500,
            py: 1,
            "&:hover": { backgroundColor: "#242930", borderColor: "#2a3040" },
          }}
        >
          Profile Page
        </Button>
        <Button
          fullWidth
          onClick={handleLogout}
          sx={{
            background: "linear-gradient(90deg, #ef4444, #f97316)",
            color: "#fff",
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 600,
            py: 1,
            "&:hover": { background: "linear-gradient(90deg, #dc2626, #ea580c)" },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        height: 64,
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 2, md: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          minHeight: "64px !important",
        }}
      >
        {/* ── Left: Hamburger (mobile) + Logo ────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Hamburger — mobile only */}
          {isMobile && (
            <IconButton
              onClick={onMobileMenuToggle}
              sx={{
                p: 1,
                borderRadius: "10px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.background.card,
                  color: theme.palette.text.primary,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo + Brand */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                borderRadius: "50%",
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.25rem", md: "1.25rem" },
                background: "linear-gradient(90deg, #05df72 0%, #00bc7d 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.022em",
                lineHeight: 1.4,
                whiteSpace: "nowrap",
              }}
            >
              StonksCentral
            </Typography>
          </Box>
        </Box>

        {/* ── Center: Search — full bar on desktop, icon on mobile ─── */}
        {!isMobile ? (
          <Box sx={{ flex: 1, mx: { xs: 1, md: 4 }, maxWidth: 480 }}>
            <CommunitySearch setCommunityName={handleSearchSelect} />
          </Box>
        ) : (
          // Spacer so right items stay right-aligned on mobile
          <Box sx={{ flex: 1 }} />
        )}

        {/* ── Right: Controls ────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>

          {/* View Mode Toggle — desktop only */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                backgroundColor: "#1a1f26",
                borderRadius: "10px",
                p: "4px",
                height: 42,
              }}
            >
              <Tooltip title="List view">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("list")}
                  sx={{
                    borderRadius: "8px",
                    width: 34,
                    height: 34,
                    backgroundColor: viewMode === "list" ? "#00c950" : "transparent",
                    color: viewMode === "list" ? "#fff" : "#6a7282",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: viewMode === "list" ? "#00b547" : "rgba(255,255,255,0.06)",
                      color: viewMode === "list" ? "#fff" : "#d1d5dc",
                    },
                  }}
                >
                  <ViewListIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tile view">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("tile")}
                  sx={{
                    borderRadius: "8px",
                    width: 34,
                    height: 34,
                    backgroundColor: viewMode === "tile" ? "#00c950" : "transparent",
                    color: viewMode === "tile" ? "#fff" : "#6a7282",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: viewMode === "tile" ? "#00b547" : "rgba(255,255,255,0.06)",
                      color: viewMode === "tile" ? "#fff" : "#d1d5dc",
                    },
                  }}
                >
                  <GridViewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Create button — desktop + logged in */}
          {isLoggedIn && !isMobile && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: "18px !important" }} />}
                onClick={handleOpenCreateMenu}
                sx={{
                  background: "linear-gradient(90deg, #00c950 0%, #009966 100%)",
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: "1rem",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 2,
                  height: 40,
                  boxShadow: "0 4px 6px rgba(0,201,80,0.2), 0 10px 15px rgba(0,201,80,0.2)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #00b547 0%, #008055 100%)",
                    boxShadow: "0 6px 12px rgba(0,201,80,0.3)",
                  },
                }}
              >
                Create
              </Button>
              <Menu
                anchorEl={anchorElCreate}
                open={Boolean(anchorElCreate)}
                onClose={handleCloseCreateMenu}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    backgroundColor: theme.palette.background.card,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "12px",
                    minWidth: 160,
                    overflow: "hidden",
                  },
                }}
              >
                <MenuItem
                  onClick={() => { navigate("/createPost/"); handleCloseCreateMenu(); }}
                  sx={{ color: theme.palette.text.primary, "&:hover": { backgroundColor: theme.palette.background.highlight } }}
                >
                  Post
                </MenuItem>
                <MenuItem
                  onClick={() => { navigate("/createCommunity/"); handleCloseCreateMenu(); }}
                  sx={{ color: theme.palette.text.primary, "&:hover": { backgroundColor: theme.palette.background.highlight } }}
                >
                  Community
                </MenuItem>
                <MenuItem
                  onClick={() => { navigate("/createPosition"); handleCloseCreateMenu(); }}
                  sx={{ color: theme.palette.text.primary, "&:hover": { backgroundColor: theme.palette.background.highlight } }}
                >
                  Position
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Search icon — mobile only */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileSearchOpen(true)}
              sx={{
                p: 1,
                borderRadius: "10px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.background.card,
                  color: theme.palette.text.primary,
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          )}

          {/* Avatar / Sign In */}
          {isLoggedIn ? (
            <Box>
              <Tooltip title="Account & notifications">
                <IconButton onClick={() => setDrawerOpen((prev) => !prev)} sx={{ p: 0.5 }}>
                  <Badge
                    color="error"
                    badgeContent={unreadNotifications.length}
                    invisible={unreadNotifications.length === 0}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  >
                    <Avatar
                      alt={user?.username}
                      src={avatarUrl || undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        border: `2px solid ${theme.palette.primary.main}`,
                      }}
                      {...(!avatarUrl && Utils.stringAvatar(user?.username))}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                {drawerContent}
              </Drawer>
            </Box>
          ) : (
            <Button
              onClick={() => navigate("/signin")}
              variant="outlined"
              sx={{
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
                color: theme.palette.primary.main,
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                px: 2,
                "&:hover": { backgroundColor: `${theme.palette.primary.main}18` },
              }}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* ── Mobile Search Dialog ───────────────────────────────── */}
      <Dialog
        open={mobileSearchOpen}
        onClose={() => { setMobileSearchOpen(false); setMobileSearchQuery(""); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f26",
            borderRadius: "14px",
            border: "1px solid #1e2939",
            mx: 2,
            mt: "80px",
            alignSelf: "flex-start",
            overflow: "visible",
          },
        }}
        sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.75)" } }}
      >
        <DialogContent sx={{ p: 0, overflow: "visible" }}>
          <Autocomplete
            open={mobileSearchResults.length > 0}
            options={mobileSearchResults}
            getOptionLabel={(o) => o.name}
            inputValue={mobileSearchQuery}
            onInputChange={(_, v) => setMobileSearchQuery(v)}
            onChange={(_, value) => {
              if (value) {
                handleSearchSelect(value.name);
                setMobileSearchOpen(false);
                setMobileSearchQuery("");
              }
            }}
            disablePortal
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "#1a1f26",
                  border: "1px solid #1e2939",
                  borderRadius: "10px",
                  mt: 0.5,
                  "& .MuiAutocomplete-option": {
                    color: "#d1d5dc",
                    fontSize: "0.875rem",
                    "&:hover, &.Mui-focused": { backgroundColor: "#242930" },
                  },
                },
              },
            }}
            renderInput={(params) => (
              <Box
                ref={params.InputProps.ref}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                <SearchIcon sx={{ color: "#6a7282", fontSize: 20, flexShrink: 0 }} />
                <InputBase
                  {...params.inputProps}
                  autoFocus
                  placeholder="Search communities or tickers…"
                  sx={{
                    flex: 1,
                    color: "#f3f4f6",
                    fontSize: "0.9rem",
                    "& input::placeholder": { color: "rgba(243,244,246,0.45)", opacity: 1 },
                  }}
                />
                <IconButton
                  onClick={() => { setMobileSearchOpen(false); setMobileSearchQuery(""); }}
                  size="small"
                  sx={{ color: "#6a7282", p: 0.5, "&:hover": { color: "#d1d5dc" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          />
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default ResponsiveTopBar;
